import React, { useCallback, useReducer, useState } from 'react'

import {
  AlignItems,
  Body2,
  Bold,
  Box,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Heading2,
  JustifyContent,
  LoadingSpinner,
  ModalProvider,
  Spinner,
  useModal,
} from '@gousto-internal/citrus-react'
import { browserHistory } from 'react-router'

import {
  ResubscribeDayConflictError,
  postSubscriptionSettingsResubscribe,
} from '@library/api-customer-engagement-experience'
import type {
  ResubscriptionBenefitsData,
  ResubscriptionCtaData,
  ResubscriptionHeroData,
  ResubscriptionOfferData,
  ResubscriptionSection,
  SettingsConfirmationData,
  SubscriptionSettingsAnalytics,
} from '@library/api-customer-engagement-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

import {
  RESUBSCRIBE_DAY_CONFLICT_ERROR_MESSAGE,
  RESUBSCRIBE_ERROR_TITLE,
  RESUBSCRIBE_GENERIC_ERROR_MESSAGE,
} from '../constants/errorCopy'
import { stageResubscriptionModal } from '../helpers/resubscriptionModalHandoff'
import { useSubscriptionSettingsResubscribe } from '../hooks/useSubscriptionSettingsResubscribe'
import { AddressModal } from './AddressModal'
import { BoxSizeModal } from './BoxSizeModal'
import { CtaButton } from './ConfirmButtons'
import { DeliveryDayModal } from './DeliveryDayModal'
import { OfferCard } from './OfferCard'
import { SettingsCard } from './SettingsCard'
import { draftReducer, getDraftInitialState, getNextBoxDateLabel } from './draftReducer'

type ResubscribeConfirmationProps = {
  userId: string
  renderFallback: () => React.ReactNode
  // Refreshes the user/subscription state the menu reads; awaited before navigating to the menu.
  onResubscribed?: () => Promise<void> | void
}

type SectionData = {
  [Section in ResubscriptionSection as Section['type']]: Section['data']
}

const findSection = <Type extends keyof SectionData>(
  sections: ResubscriptionSection[],
  type: Type,
) => sections.find((section) => section.type === type)?.data as SectionData[Type] | undefined

export const ResubscribeConfirmation = ({
  userId,
  renderFallback,
  onResubscribed,
}: ResubscribeConfirmationProps) => {
  const { sections, isLoading } = useSubscriptionSettingsResubscribe(userId)

  const hero = sections && findSection(sections, 'resubscription_hero')
  const offer = sections && findSection(sections, 'resubscription_offer')
  const settings = sections && findSection(sections, 'settings_confirmation')
  const benefits = sections && findSection(sections, 'resubscription_benefits_web')
  const cta = sections && findSection(sections, 'resubscription_cta')

  if (isLoading) {
    return (
      <Box
        data-testid="resubscribe-confirmation-loading"
        aria-busy="true"
        display={Display.Flex}
        justifyContent={JustifyContent.Center}
        paddingV={4}
        paddingH={4}
      >
        <LoadingSpinner />
      </Box>
    )
  }

  if (!hero || !offer || !settings || !benefits || !cta) {
    return <>{renderFallback()}</>
  }

  return (
    <ModalProvider>
      <ResubscribeConfirmationScreen
        hero={hero}
        offer={offer}
        settings={settings}
        benefits={benefits}
        cta={cta}
        onResubscribed={onResubscribed}
      />
    </ModalProvider>
  )
}

type ResubscribeConfirmationScreenProps = {
  hero: ResubscriptionHeroData
  offer: ResubscriptionOfferData
  settings: SettingsConfirmationData
  benefits: ResubscriptionBenefitsData
  cta: ResubscriptionCtaData
  onResubscribed?: () => Promise<void> | void
}

const ResubscribeConfirmationScreen = ({
  hero,
  offer,
  settings,
  benefits,
  cta,
  onResubscribed,
}: ResubscribeConfirmationScreenProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<'day_conflict' | 'generic' | null>(null)
  const [draft, dispatchDraft] = useReducer(draftReducer, getDraftInitialState(settings))
  const { openModal } = useModal()

  // Addresses with no delivery days aren't offered.
  const addressOptions = settings.address.options.filter(
    (address) => address.deliveryDayOptions.length > 0,
  )
  const draftedAddress = draft.address
  const draftedBoxSizeLabel = settings.boxSize.options.find(
    (option) => option.numPortions === draft.boxSize.numPortions,
  )?.label

  const onEditAction = useCallback(
    (
      editAction: { intent: string; title: string; analytics?: SubscriptionSettingsAnalytics },
      modalName?: string,
    ) => {
      if (editAction.analytics) {
        queueTrackingEvent<SubscriptionSettingsAnalytics>(editAction.analytics)
      }
      if (modalName) {
        openModal(modalName)
      }
    },
    [openModal],
  )

  const onConfirm = useCallback(async () => {
    if (cta.analytics) {
      queueTrackingEvent<SubscriptionSettingsAnalytics>(cta.analytics)
    }

    setError(null)
    setIsSubmitting(true)

    try {
      const result = await postSubscriptionSettingsResubscribe({
        daySlotLeadTimeId: draft.day.daySlotLeadTimeId,
        addressId: draft.address.addressId,
        boxSize: draft.boxSize,
      })

      if (result.reactivate) {
        queueTrackingEvent<SubscriptionSettingsAnalytics>(result.reactivate)
      }

      try {
        await onResubscribed?.()
      } catch {
        // The customer is already reactivated, so a failed refresh doesn't stop the navigation.
      }

      if (result.orderId) {
        await stageResubscriptionModal()
        browserHistory.push(`/menu/${result.orderId}`)
      } else {
        browserHistory.push('/my-deliveries')
      }
    } catch (submitError) {
      setError(submitError instanceof ResubscribeDayConflictError ? 'day_conflict' : 'generic')
      setIsSubmitting(false)
    }
  }, [cta, draft, onResubscribed])

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      paddingH={[4, 4, 6, 8]}
      paddingV={[4, 4, 6, 8]}
    >
      <Heading2 size={10} color={Color.ColdGrey_800}>
        {hero.headline}
      </Heading2>

      <Box
        display={Display.Flex}
        flexDirection={[
          FlexDirection.Column,
          FlexDirection.Column,
          FlexDirection.Column,
          FlexDirection.Row,
        ]}
        alignItems={AlignItems.Stretch}
        gap={[4, 4, 4, 8]}
      >
        <OfferCard
          offer={offer}
          nextBoxDateLabel={getNextBoxDateLabel(
            draft.day,
            settings.deliveryDay.current,
            offer.next_box_date_label,
          )}
          discount={draft.day.discount}
          benefits={benefits.benefits}
        />

        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.SpaceBetween}
          gap={4}
          width={['100%', '100%', '100%', '50%']}
        >
          <SettingsCard
            settings={settings}
            dayLabel={draft.day.label}
            address={draftedAddress}
            boxSizeLabel={draftedBoxSizeLabel}
            onEditAction={onEditAction}
          />

          {error && (
            <Box
              role="alert"
              bg={Color.Error_50}
              borderColor={Color.Error_900}
              paddingV={3}
              paddingH={3}
              borderRadius={1.5}
              data-testid="error-panel"
            >
              <Body2>
                <Bold>{RESUBSCRIBE_ERROR_TITLE}</Bold>
              </Body2>
              <Body2>
                {error === 'day_conflict'
                  ? RESUBSCRIBE_DAY_CONFLICT_ERROR_MESSAGE
                  : RESUBSCRIBE_GENERIC_ERROR_MESSAGE}
              </Body2>
            </Box>
          )}

          <CtaButton
            colorVariant={ButtonColorVariant.Primary}
            onClick={onConfirm}
            disabled={isSubmitting}
            width="100%"
          >
            <Box
              display={Display.Flex}
              justifyContent={JustifyContent.Center}
              alignItems={AlignItems.Center}
              gap={2}
            >
              {isSubmitting && (
                <Box data-testid="resubscribe-confirmation-submitting" aria-hidden="true">
                  <Spinner size={4} color={Color.White} />
                </Box>
              )}
              {cta.title}
            </Box>
          </CtaButton>
        </Box>
      </Box>

      <DeliveryDayModal
        options={draftedAddress.deliveryDayOptions}
        selectedDaySlotLeadTimeId={draft.day.daySlotLeadTimeId}
        title={settings.deliveryDay.pickerTitle}
        deliveryDayRangeLabel={draftedAddress.deliveryDayRangeLabel}
        onSelect={(daySlotLeadTimeId) => dispatchDraft({ type: 'selectDay', daySlotLeadTimeId })}
      />
      <BoxSizeModal
        options={settings.boxSize.options}
        selectedBoxSize={draft.boxSize}
        title={settings.boxSize.title}
        suitableForTitle={settings.boxSize.suitableForTitle}
        saveLabel={settings.boxSize.saveLabel}
        onSelect={(boxSize) => dispatchDraft({ type: 'selectBoxSize', boxSize })}
      />
      <AddressModal
        options={addressOptions}
        selectedAddressId={draft.address.addressId}
        title={settings.address.pickerTitle}
        subtitle={settings.address.pickerSubtitle}
        onSelect={(address) => dispatchDraft({ type: 'selectAddress', address })}
      />
    </Box>
  )
}
